import { request, uploadFile } from "../base";
import type {
	Blog,
	BlogCategory,
	BlogTag,
	CreateBlogInput,
	UpdateBlogInput,
	AddBlogProInput,
	AddBlogProjectInput,
	BlogListFilters,
	ProBlogSuggestion,
} from "../blogs";
import type {
	BlogImage,
	AddPexelsImageInput,
	UpdateBlogImageInput,
	BlogMediaItem,
	BlogMediaPage,
} from "../blog-images";

export const adminBlogsApi = {
	async listBlogs(filters?: BlogListFilters) {
		const params = new URLSearchParams();
		if (filters) {
			Object.entries(filters).forEach(([key, value]) => {
				if (value !== undefined) {
					params.append(key, String(value));
				}
			});
		}
		const query = params.toString() ? `?${params.toString()}` : "";
		return request<Blog[]>(`/api/admin/blogs${query}`);
	},

	async checkBlogSlug(slug: string, excludeId?: string) {
		const params = new URLSearchParams({ slug });
		if (excludeId) params.set("excludeId", excludeId);
		return request<{ available: boolean }>(
			`/api/admin/blogs/check-slug?${params.toString()}`,
		);
	},

	async getBlog(id: string) {
		return request<Blog>(`/api/admin/blogs/${id}`);
	},

	async createBlog(data: CreateBlogInput) {
		return request<Blog>(`/api/admin/blogs`, {
			method: "POST",
			body: data,
		});
	},

	async updateBlog(id: string, data: UpdateBlogInput) {
		return request<Blog>(`/api/admin/blogs/${id}`, {
			method: "PUT",
			body: data,
		});
	},

	async deleteBlog(id: string) {
		return request(`/api/admin/blogs/${id}`, {
			method: "DELETE",
		});
	},

	async publishBlog(id: string) {
		return request<Blog>(`/api/admin/blogs/${id}/publish`, {
			method: "POST",
		});
	},

	async unpublishBlog(id: string) {
		return request<Blog>(`/api/admin/blogs/${id}/unpublish`, {
			method: "POST",
		});
	},

	// Cover Image Upload
	async uploadBlogCover(blogId: string, file: File, altText?: string) {
		return uploadFile<{ blog: Blog; imageUrl: string }>(
			`/api/admin/blogs/${blogId}/upload-cover`,
			file,
			altText ? { altText } : undefined,
		);
	},

	// Blog Pros
	async addBlogPro(blogId: string, data: AddBlogProInput) {
		return request(`/api/admin/blogs/${blogId}/pros`, {
			method: "POST",
			body: data,
		});
	},

	async removeBlogPro(blogId: string, proId: string) {
		return request(`/api/admin/blogs/${blogId}/pros/${proId}`, {
			method: "DELETE",
		});
	},

	async updateBlogPro(
		blogId: string,
		proId: string,
		data: Partial<AddBlogProInput>,
	) {
		return request(`/api/admin/blogs/${blogId}/pros/${proId}`, {
			method: "PUT",
			body: data,
		});
	},

	// Blog Projects
	async addBlogProject(blogId: string, data: AddBlogProjectInput) {
		return request(`/api/admin/blogs/${blogId}/projects`, {
			method: "POST",
			body: data,
		});
	},

	async removeBlogProject(blogId: string, projectId: string) {
		return request(`/api/admin/blogs/${blogId}/projects/${projectId}`, {
			method: "DELETE",
		});
	},

	// Blog Categories
	async listBlogCategories(includeInactive = false) {
		const query = includeInactive ? "?includeInactive=true" : "";
		return request<BlogCategory[]>(`/api/admin/blogs/categories/all${query}`);
	},

	async createBlogCategory(data: Partial<BlogCategory>) {
		return request<BlogCategory>(`/api/admin/blogs/categories`, {
			method: "POST",
			body: data,
		});
	},

	async updateBlogCategory(id: string, data: Partial<BlogCategory>) {
		return request<BlogCategory>(`/api/admin/blogs/categories/${id}`, {
			method: "PUT",
			body: data,
		});
	},

	async deleteBlogCategory(id: string) {
		return request(`/api/admin/blogs/categories/${id}`, {
			method: "DELETE",
		});
	},

	// Blog Tags
	async listBlogTags() {
		return request<BlogTag[]>(`/api/admin/blogs/tags/all`);
	},

	async createBlogTag(data: Partial<BlogTag>) {
		return request<BlogTag>(`/api/admin/blogs/tags`, {
			method: "POST",
			body: data,
		});
	},

	async updateBlogTag(id: string, data: Partial<BlogTag>) {
		return request<BlogTag>(`/api/admin/blogs/tags/${id}`, {
			method: "PUT",
			body: data,
		});
	},

	async deleteBlogTag(id: string) {
		return request(`/api/admin/blogs/tags/${id}`, {
			method: "DELETE",
		});
	},

	// Blog Suggestions
	async listBlogSuggestions(status?: string) {
		const query = status ? `?status=${status}` : "";
		return request<ProBlogSuggestion[]>(
			`/api/admin/blogs/suggestions/all${query}`,
		);
	},

	async listProBlogSuggestions(filters?: { status?: string }) {
		return adminBlogsApi.listBlogSuggestions(filters?.status);
	},

	async acceptBlogSuggestion(id: string, adminNotes?: string) {
		return request<ProBlogSuggestion>(
			`/api/admin/blogs/suggestions/${id}/accept`,
			{
				method: "PUT",
				body: { adminNotes },
			},
		);
	},

	async rejectBlogSuggestion(id: string, adminNotes: string) {
		return request<ProBlogSuggestion>(
			`/api/admin/blogs/suggestions/${id}/reject`,
			{
				method: "PUT",
				body: { adminNotes },
			},
		);
	},

	async updateProBlogSuggestion(
		id: string,
		data: {
			status?: "pending" | "accepted" | "rejected" | "published";
			acceptedBlogId?: string;
			adminNotes?: string;
		},
	) {
		return request<ProBlogSuggestion>(`/api/admin/blogs/suggestions/${id}`, {
			method: "PUT",
			body: data,
		});
	},

	// ── Blog Image Methods (admin) ────────────────────────────────────────────

	async getAdminBlogImages(blogId: string) {
		return request<BlogImage[]>(`/api/admin/blogs/${blogId}/images`);
	},

	async uploadAdminBlogImage(
		blogId: string,
		file: File,
		extra?: { altText?: string; caption?: string },
	) {
		const fields: Record<string, string> = {};
		if (extra?.altText) fields.altText = extra.altText;
		if (extra?.caption) fields.caption = extra.caption;
		return uploadFile<BlogImage>(
			`/api/admin/blogs/${blogId}/images/upload`,
			file,
			Object.keys(fields).length ? fields : undefined,
		);
	},

	async addAdminPexelsImage(blogId: string, input: AddPexelsImageInput) {
		return request<BlogImage>(`/api/admin/blogs/${blogId}/images/pexels`, {
			method: "POST",
			body: {
				pexelsId: input.pexelsId,
				url: input.url,
				width: input.width,
				height: input.height,
				altText: input.altText,
				photographer: input.photographer,
				photographerUrl: input.photographerUrl,
			},
		});
	},

	async addAdminMediaImage(
		blogId: string,
		input: { mediaId: number; altText?: string; caption?: string },
	) {
		return request<BlogImage>(`/api/admin/blogs/${blogId}/images/media`, {
			method: "POST",
			body: {
				mediaId: input.mediaId,
				...(input.altText !== undefined && { altText: input.altText }),
				...(input.caption !== undefined && { caption: input.caption }),
			},
		});
	},

	async searchAdminPexels(query: string, page = 1, perPage = 20) {
		const params = new URLSearchParams({
			q: query,
			page: page.toString(),
			per_page: perPage.toString(),
		});
		return request(
			`/api/admin/blogs/images/search/pexels?${params.toString()}`,
		);
	},

	async searchAllProsMedia(
		_blogId: string,
		params: {
			search?: string;
			proId?: string;
			page?: number;
			perPage?: number;
		} = {},
	): Promise<BlogMediaPage> {
		const { search = "", proId, page = 1, perPage = 20 } = params;
		const qs = new URLSearchParams({
			page: page.toString(),
			per_page: perPage.toString(),
		});
		if (search.trim()) qs.set("q", search.trim());
		if (proId) qs.set("proId", proId);
		const response = await request<BlogMediaItem[]>(
			`/api/admin/blogs/images/gallery?${qs.toString()}`,
		);
		return {
			data: response.data ?? [],
			meta: {
				total: response.meta?.total ?? 0,
				page: response.meta?.page ?? page,
				limit: response.meta?.limit ?? perPage,
				totalPages: response.meta?.totalPages ?? 1,
				hasNext: response.meta?.hasNext ?? false,
				hasPrev: response.meta?.hasPrev ?? false,
			},
		};
	},

	async deleteAdminBlogImage(blogId: string, imageId: number) {
		return request<{ message: string }>(
			`/api/admin/blogs/${blogId}/images/${imageId}`,
			{ method: "DELETE" },
		);
	},

	async updateAdminBlogImage(
		blogId: string,
		imageId: number,
		input: UpdateBlogImageInput,
	) {
		return request<BlogImage>(`/api/admin/blogs/${blogId}/images/${imageId}`, {
			method: "PUT",
			body: input,
		});
	},
};
